import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getModelConfig,
  getAvailableModels,
  setModelConfig,
  getWorkflowConfigs,
  saveWorkflowConfig,
  getWorkflowConfigHistory,
  activateWorkflowConfig,
  type ModelConfig,
  type RAGWorkflowConfig,
  type SourceWeightsConfig,
  type WorkflowStageConfig,
  type WorkflowConfigHistoryItem,
} from "@/lib/api";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Check,
  Zap,
  Clock,
  Snail,
  DollarSign,
  Brain,
  Wrench,
  Save,
  Gift,
  Settings2,
  Scale,
  History,
  RotateCcw,
  Search,
  MessageSquare,
  FileText,
  ChevronRight,
  Eye,
  EyeOff,
  Pencil,
  RotateCw,
  Type,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// CONSTANTS
// ============================================================================

const speedConfig = {
  free: { icon: Gift, label: "Free", color: "text-blue-500", bgColor: "bg-blue-500/10" },
  fast: { icon: Zap, label: "Fast", color: "text-green-500", bgColor: "bg-green-500/10" },
  medium: { icon: Clock, label: "Medium", color: "text-yellow-500", bgColor: "bg-yellow-500/10" },
  slow: { icon: Snail, label: "Slow", color: "text-orange-500", bgColor: "bg-orange-500/10" },
};

const providerColors: Record<string, string> = {
  OpenAI: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100",
  Anthropic: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100",
  Google: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
  Meta: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100",
  DeepSeek: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100",
  Alibaba: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
  xAI: "bg-stone-100 text-stone-800 dark:bg-stone-900 dark:text-stone-100",
};

const sourceInfo: Record<string, { name: string; description: string; color: string }> = {
  golden: { name: "Golden answers", description: "Curated Q&A pairs with verified answers", color: "text-yellow-600" },
  help: { name: "Help center", description: "Intercom help articles", color: "text-blue-600" },
  linear: { name: "Linear", description: "Project management issues and updates", color: "text-purple-600" },
  notion: { name: "Notion", description: "Documentation and wiki pages", color: "text-gray-600" },
  slack: { name: "Slack", description: "Historical conversation threads", color: "text-green-600" },
};

const stageInfo = {
  queryExpander: {
    name: "Query expander",
    description: "Expands and enriches user queries with context and abbreviations",
    icon: Search,
  },
  assessor: {
    name: "Assessor",
    description: "Checks if query is relevant to Atlas when no results are found",
    icon: Scale,
  },
  reasoner: {
    name: "Reasoner",
    description: "Analyzes retrieved documents and formulates accurate answers",
    icon: Brain,
  },
  formatter: {
    name: "Formatter",
    description: "Converts answers to Slack-formatted responses with citations",
    icon: FileText,
  },
};

// Default prompts for each agent stage
// These match the defaults in rag-workflow.ts
const DEFAULT_PROMPTS: Record<keyof typeof stageInfo, string> = {
  queryExpander: `You are the Query Expander for an internal knowledge assistant.

## Inputs
- userMessage (string)
- threadMessages (optional array, oldest → newest)

## Tasks
1. Decide if retrieval should run (shouldProceed)
2. Expand glossary acronyms using BOTH short and full forms (only when present)
3. Resolve follow-ups using thread context (if applicable)
4. Extract high-signal searchTerms for hybrid search

## Domain Glossary (expand only when encountered)
Include BOTH forms in searchTerms when these appear:
- MDR → Merchant Discount Rate (fee charged to merchants for payments)
- POS → Point of Sale (where transactions are completed)
- GMV → Gross Merchandise Value (total sales value)
- SKU → Stock Keeping Unit (unique product identifier)
- COD → Cash on Delivery
- OOS → Out of Stock
- 3PL → Third Party Logistics
- AOV → Average Order Value
- FP → Foodpanda
- GF → GrabFood
- STO → Scan To Order

Do NOT guess expansions for acronyms not in this list.

## Output Format (valid JSON only, no extra keys)
{
  "shouldProceed": boolean,
  "exitReason": null | "greeting" | "acknowledgement" | "off_topic" | "unclear" | "inappropriate",
  "enrichedQuery": string | null,
  "searchTerms": array | null,
  "isFollowUp": boolean,
  "contextSummary": string | null
}

## Decision Rules

### shouldProceed = true when:
- User mentions a topic/subject, even if phrased as "Can I ask about X?" or "I have questions about X"
- These are TOPIC INDICATORS, not just greetings — treat as questions about X
- Example: "Can I ask about special hours?" → shouldProceed=true, search for special hours info

### shouldProceed = false when:
- Greeting only with NO topic ("hi", "hello", "hey there")
- Acknowledgement only ("thanks", "ok", "got it", "👍", emoji reactions)
- Gibberish / non-language / too vague to search
- Inappropriate content
- Clearly unrelated to domain (off_topic)

When shouldProceed = false:
- Set appropriate exitReason
- enrichedQuery = null
- searchTerms = null

### Follow-up Handling
- isFollowUp = true ONLY if the query depends on thread context ("what about X?", "and for FP?", "does that work for POS?")
- If query is a CLEAR TOPIC SWITCH, set isFollowUp = false and ignore thread context
- When isFollowUp = true, enrichedQuery must be standalone (include the subject from context)
- contextSummary: 1 short sentence describing thread topic (only when isFollowUp = true)

## enrichedQuery Guidelines
- Should read naturally and be explicit
- Expand glossary acronyms inline: "MDR (Merchant Discount Rate)"
- Include concrete entities: platform names, feature names, UI labels, error codes
- Stay close to user's original intent — do NOT add assumptions or invented context

## searchTerms Guidelines
- 3-5 high-signal terms
- Priority order: platform > feature > object > action
- Preserve exact casing for error codes, IDs, filenames, endpoints
- Action verbs ARE allowed (block, refund, void, change, configure)
- Avoid only generic stopwords: "how", "what", "the", "is", "please", "can", "do"
- Include both acronym + expansion when glossary term is present
- Deduplicate

## Examples

Query: "How do I change MDR for GrabFood?"
{
  "shouldProceed": true,
  "exitReason": null,
  "enrichedQuery": "How to change MDR (Merchant Discount Rate) for GrabFood",
  "searchTerms": ["MDR", "Merchant Discount Rate", "GrabFood", "change"],
  "isFollowUp": false,
  "contextSummary": null
}

Thread: "How do I block items?" → assistant answered
Query: "What about FP?"
{
  "shouldProceed": true,
  "exitReason": null,
  "enrichedQuery": "How to block items on Foodpanda (FP)",
  "searchTerms": ["block", "items", "Foodpanda", "FP"],
  "isFollowUp": true,
  "contextSummary": "User asking about blocking items, now for Foodpanda"
}

Query: "Hey!"
{
  "shouldProceed": false,
  "exitReason": "greeting",
  "enrichedQuery": null,
  "searchTerms": null,
  "isFollowUp": false,
  "contextSummary": null
}

Query: "I need help with refunds, can I ask here?"
{
  "shouldProceed": true,
  "exitReason": null,
  "enrichedQuery": "How to process refunds",
  "searchTerms": ["refund", "process"],
  "isFollowUp": false,
  "contextSummary": null
}

Respond ONLY with valid JSON.`,

  assessor: `You are assessing if a user's question is relevant to Atlas restaurant operating system.

Atlas topics include:
- Menu management, items, categories, modifiers
- Orders, delivery, third-party platforms (GrabFood, Foodpanda, Deliveroo)
- Stocks, inventory, blocking/unblocking items
- POS systems, terminals, printers
- Merchant portal, reports, analytics
- Store settings, integrations, promotions

Off-topic includes:
- General tech questions not about Atlas
- Personal questions, jokes, random chat
- Questions about other companies/products
- Coding/programming questions

Question: "{query}"

Is this question relevant to Atlas? Respond with ONLY "yes" or "no".`,

  reasoner: `You are analyzing search results to answer a question about Atlas restaurant system.

Analyze the search results carefully and create a comprehensive, accurate answer.

RULES:
- Use ONLY information from the search results
- Do NOT hallucinate or make up information
- If the search results don't fully answer the question, say what's missing
- Include all relevant steps and details
- Cite your sources by mentioning where the info came from

Question: {query}

Search Results:
{results}

Provide a thorough, accurate answer based on the search results above. Structure it clearly with steps or bullet points where appropriate. Be specific about UI elements, buttons, and navigation paths mentioned in the sources.`,

  formatter: `Convert this answer to Slack mrkdwn format.

INPUT ANSWER:
{answer}

SOURCE URLS:
{sources}

OUTPUT RULES:
- Bold header: *text* (single asterisks)
- Links: <URL|:emoji: Title> format
- Numbered lists for steps (1. 2. 3.)
- Blank line after header
- Source link on its OWN line at the very end
- NO HTML entities like &gt;
- Keep it concise but complete

SOURCE EMOJI:
- Help Center: :atlas-light:
- Linear: :linear:
- Slack: :slack:
- Notion: :notion:

Convert the answer to Slack format now:`,
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatContextWindow(tokens: number): string {
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  if (tokens >= 1000) return `${Math.round(tokens / 1000)}K`;
  return tokens.toString();
}

function formatCostPerMillion(costPer1k: number): string {
  const costPerMillion = costPer1k * 1000;
  return `$${costPerMillion.toFixed(2)}`;
}

function calculateDelta(
  modelCost: number,
  currentCost: number
): { percent: number; label: string; color: string } | null {
  if (modelCost === currentCost) {
    return { percent: 0, label: "Same price", color: "text-muted-foreground" };
  }

  const percentDiff = ((modelCost - currentCost) / currentCost) * 100;

  if (percentDiff < 0) {
    return {
      percent: Math.abs(percentDiff),
      label: `${Math.abs(percentDiff).toFixed(0)}% cheaper`,
      color: "text-green-600",
    };
  }
  return {
    percent: percentDiff,
    label: `${percentDiff.toFixed(0)}% more`,
    color: "text-red-600",
  };
}

// ============================================================================
// MODEL CARD COMPONENT (for single model selection tab)
// ============================================================================

function ModelCard({
  model,
  currentModel,
  isSelected,
  isCurrent,
  isChanging,
  onSelect,
}: {
  model: ModelConfig;
  currentModel?: ModelConfig;
  isSelected: boolean;
  isCurrent: boolean;
  isChanging: boolean;
  onSelect: () => void;
}) {
  const speed = speedConfig[model.speed];
  const SpeedIcon = speed.icon;

  const inputDelta =
    currentModel && !isCurrent
      ? calculateDelta(model.costPer1kInput, currentModel.costPer1kInput)
      : null;
  const outputDelta =
    currentModel && !isCurrent
      ? calculateDelta(model.costPer1kOutput, currentModel.costPer1kOutput)
      : null;

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        isSelected && "ring-2 ring-primary",
        isChanging && "opacity-50"
      )}
      onClick={() => !isChanging && onSelect()}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              {model.name}
              {isSelected && <Check className="h-4 w-4 text-primary" />}
              {isCurrent && !isSelected && (
                <Badge variant="outline" className="ml-1 text-xs">
                  Current
                </Badge>
              )}
            </CardTitle>
            <Badge className={providerColors[model.provider] || ""}>
              {model.provider}
            </Badge>
          </div>
          <div className={cn("p-1.5 rounded", speed.bgColor)}>
            <SpeedIcon className={cn("h-4 w-4", speed.color)} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{model.description}</p>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5">
            <Brain className="h-3 w-3 text-muted-foreground" />
            <span>{formatContextWindow(model.contextWindow)} context</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Wrench className="h-3 w-3 text-muted-foreground" />
            <span>{model.toolSupport ? "Tool support" : "No tools"}</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs">
            <DollarSign className="h-3 w-3 text-muted-foreground shrink-0" />
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs">
                In: {formatCostPerMillion(model.costPer1kInput)}/M
              </span>
              <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-xs">
                Out: {formatCostPerMillion(model.costPer1kOutput)}/M
              </span>
            </div>
          </div>
          {(inputDelta || outputDelta) && (
            <div className="flex items-center gap-1.5 text-xs pl-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                {inputDelta && (
                  <span className={cn("text-xs", inputDelta.color)}>
                    In: {inputDelta.label}
                  </span>
                )}
                {outputDelta && (
                  <span className={cn("text-xs", outputDelta.color)}>
                    Out: {outputDelta.label}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MODEL SELECTOR MODAL (for RAG pipeline stage model selection)
// ============================================================================

function ModelSelectorModal({
  open,
  onOpenChange,
  models,
  selectedModelId,
  onSelect,
  stageName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  models: ModelConfig[];
  selectedModelId: string;
  onSelect: (modelId: string) => void;
  stageName: string;
}) {
  const freeModels = models.filter((m) => m.speed === "free");
  const fastModels = models.filter((m) => m.speed === "fast");
  const mediumModels = models.filter((m) => m.speed === "medium");
  const slowModels = models.filter((m) => m.speed === "slow");

  const handleSelect = (modelId: string) => {
    onSelect(modelId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Model for {stageName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {[
            { models: freeModels, icon: Gift, title: "Free Models", color: "text-blue-500", desc: "Experimental or free-tier" },
            { models: fastModels, icon: Zap, title: "Fast Models", color: "text-green-500", desc: "Best for quick responses" },
            { models: mediumModels, icon: Clock, title: "Balanced Models", color: "text-yellow-500", desc: "Good balance" },
            { models: slowModels, icon: Brain, title: "Premium Models", color: "text-purple-500", desc: "Best quality" },
          ].map(({ models: categoryModels, icon: Icon, title, color, desc }) =>
            categoryModels.length > 0 ? (
              <div key={title} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className={cn("h-4 w-4", color)} />
                  <h3 className="text-lg font-semibold">{title}</h3>
                  <span className="text-sm text-muted-foreground">{desc}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {categoryModels.map((model) => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      isSelected={model.id === selectedModelId}
                      isCurrent={false}
                      isChanging={false}
                      onSelect={() => handleSelect(model.id)}
                    />
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// PROMPT EDITOR MODAL
// ============================================================================

function PromptEditorModal({
  open,
  onOpenChange,
  stage,
  prompt,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stage: keyof typeof stageInfo;
  prompt: string;
  onSave: (prompt: string) => void;
}) {
  const info = stageInfo[stage];
  const Icon = info.icon;
  const defaultPrompt = DEFAULT_PROMPTS[stage];
  
  // The effective prompt is either the custom prompt or the default
  const effectivePrompt = prompt || defaultPrompt;
  const [localPrompt, setLocalPrompt] = useState(effectivePrompt);
  const hasChanges = localPrompt !== effectivePrompt;

  // Reset local state when modal opens or stage changes
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setLocalPrompt(prompt || defaultPrompt);
    }
    onOpenChange(newOpen);
  };

  const handleSave = () => {
    // If prompt is the same as default, save as empty/undefined to use default
    const promptToSave = localPrompt === defaultPrompt ? "" : localPrompt;
    onSave(promptToSave);
    onOpenChange(false);
  };

  const handleResetToDefault = () => {
    setLocalPrompt(defaultPrompt);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            Edit {info.name} Prompt
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {info.description}
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium">System Prompt</Label>
              {localPrompt === defaultPrompt ? (
                <Badge variant="secondary" className="text-xs">
                  Default
                </Badge>
              ) : (
                <Badge className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                  Custom
                </Badge>
              )}
              {hasChanges && (
                <Badge variant="outline" className="text-xs">
                  Unsaved
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Type className="h-3 w-3" />
              {localPrompt.length} characters
            </div>
          </div>
          
          <textarea
            value={localPrompt}
            onChange={(e) => setLocalPrompt(e.target.value)}
            className={cn(
              "w-full h-[400px] p-4 rounded-lg border bg-muted/30 resize-none",
              "font-mono text-sm leading-relaxed",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent",
              "placeholder:text-muted-foreground"
            )}
            placeholder="Enter the system prompt for this agent..."
          />

          <p className="text-xs text-muted-foreground">
            Use {"{query}"} for user query, {"{results}"} for search results, {"{answer}"} for reasoner output, {"{sources}"} for source URLs.
          </p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleResetToDefault}
            disabled={localPrompt === defaultPrompt}
            className="gap-2"
          >
            <RotateCw className="h-4 w-4" />
            Reset to Default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges}>
              <Save className="h-4 w-4 mr-2" />
              Save Prompt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MODELS TAB (existing functionality)
// ============================================================================

function ModelsTab() {
  const queryClient = useQueryClient();

  const { data: currentConfig, isLoading: configLoading } = useQuery({
    queryKey: ["model-config"],
    queryFn: getModelConfig,
  });

  const { data: availableModels, isLoading: modelsLoading } = useQuery({
    queryKey: ["available-models"],
    queryFn: getAvailableModels,
  });

  const [localSelectedModelId, setLocalSelectedModelId] = useState<string | null>(null);
  const selectedModelId = localSelectedModelId ?? currentConfig?.selectedModelId ?? null;
  const setSelectedModelId = setLocalSelectedModelId;

  const setModelMutation = useMutation({
    mutationFn: setModelConfig,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["model-config"] });
      setSelectedModelId(data.selectedModelId);
    },
  });

  const handleSelectModel = (modelId: string) => {
    setSelectedModelId(modelId);
  };

  const handleUpdateModel = () => {
    if (
      selectedModelId &&
      localSelectedModelId !== null &&
      localSelectedModelId !== currentConfig?.selectedModelId
    ) {
      setModelMutation.mutate(selectedModelId);
    }
  };

  const hasChanges =
    localSelectedModelId !== null && localSelectedModelId !== currentConfig?.selectedModelId;
  const isLoading = configLoading || modelsLoading;

  const freeModels = availableModels?.models.filter((m) => m.speed === "free") || [];
  const fastModels = availableModels?.models.filter((m) => m.speed === "fast") || [];
  const mediumModels = availableModels?.models.filter((m) => m.speed === "medium") || [];
  const slowModels = availableModels?.models.filter((m) => m.speed === "slow") || [];

  return (
    <div className="space-y-6">
      {isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>Single Model RAG (Legacy)</CardTitle>
            <CardDescription>
              Select the default model for single-model mode (used when RAG pipeline is
              disabled)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Model</p>
                <Skeleton className="h-5 w-40 mt-1" />
              </div>
            </div>
            <Skeleton className="h-9 w-32" />
          </CardContent>
        </Card>
      ) : currentConfig && (
        <Card>
          <CardHeader>
            <CardTitle>Single Model RAG (Legacy)</CardTitle>
            <CardDescription>
              Select the default model for single-model mode (used when RAG pipeline is
              disabled)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Model</p>
                <p className="font-medium">{currentConfig.model.name}</p>
              </div>
              {hasChanges && selectedModelId && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Changing to</p>
                  <p className="font-medium text-primary">
                    {availableModels?.models.find((m) => m.id === selectedModelId)?.name}
                  </p>
                </div>
              )}
            </div>
            <Button
              onClick={handleUpdateModel}
              disabled={!hasChanges || setModelMutation.isPending}
            >
              {setModelMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Update Model
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {setModelMutation.isSuccess && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-700 dark:text-green-400 text-sm">
          Model updated successfully.
        </div>
      )}

      {setModelMutation.isError && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          Failed to update model.
        </div>
      )}

      {isLoading ? (
        <div className="space-y-8">
          {[
            { icon: Gift, title: "Free Models", color: "text-blue-500", desc: "Experimental or free-tier", count: 2 },
            { icon: Zap, title: "Fast Models", color: "text-green-500", desc: "Best for quick responses", count: 3 },
            { icon: Clock, title: "Balanced Models", color: "text-yellow-500", desc: "Good balance", count: 3 },
          ].map(({ icon: Icon, title, color, desc, count }) => (
            <div key={title} className="space-y-4">
              <div className="flex items-center gap-2">
                <Icon className={cn("h-5 w-5", color)} />
                <h2 className="text-xl font-semibold">{title}</h2>
                <span className="text-sm text-muted-foreground">{desc}</span>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: count }).map((_, i) => (
                  <ModelCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {[
            { models: freeModels, icon: Gift, title: "Free Models", color: "text-blue-500", desc: "Experimental or free-tier" },
            { models: fastModels, icon: Zap, title: "Fast Models", color: "text-green-500", desc: "Best for quick responses" },
            { models: mediumModels, icon: Clock, title: "Balanced Models", color: "text-yellow-500", desc: "Good balance" },
            { models: slowModels, icon: Brain, title: "Premium Models", color: "text-purple-500", desc: "Best quality" },
          ].map(({ models, icon: Icon, title, color, desc }) =>
            models.length > 0 ? (
              <div key={title} className="space-y-4">
                <div className="flex items-center gap-2">
                  <Icon className={cn("h-5 w-5", color)} />
                  <h2 className="text-xl font-semibold">{title}</h2>
                  <span className="text-sm text-muted-foreground">{desc}</span>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {models.map((model) => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      currentModel={currentConfig?.model}
                      isSelected={model.id === selectedModelId}
                      isCurrent={model.id === currentConfig?.selectedModelId}
                      isChanging={setModelMutation.isPending}
                      onSelect={() => handleSelectModel(model.id)}
                    />
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {hasChanges && (
        <Button
          variant="outline"
          onClick={() => setLocalSelectedModelId(null)}
          disabled={setModelMutation.isPending}
        >
          Cancel Changes
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// STAGE CONFIG EDITOR
// ============================================================================

function StageConfigEditor({
  stage,
  config,
  models,
  onChange,
  readOnly = false,
}: {
  stage: keyof typeof stageInfo;
  config: WorkflowStageConfig;
  models: ModelConfig[];
  onChange: (config: WorkflowStageConfig) => void;
  readOnly?: boolean;
}) {
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const info = stageInfo[stage];
  const Icon = info.icon;
  const selectedModel = models.find((m) => m.id === config.modelId);

  const speedInfo = selectedModel ? speedConfig[selectedModel.speed] : null;
  const SpeedIcon = speedInfo?.icon;

  // Check if using custom prompt
  const defaultPrompt = DEFAULT_PROMPTS[stage];
  const currentPrompt = config.prompt || "";
  const isUsingCustomPrompt = currentPrompt && currentPrompt !== defaultPrompt;

  return (
    <>
      <Card className={cn(readOnly && "opacity-80")}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Icon className="h-5 w-5" />
            {info.name}
          </CardTitle>
          <CardDescription>{info.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Model Selector */}
          <div className="space-y-1.5">
            <Label className="text-xs">Model</Label>
            <div
              className={cn(
                "flex items-center gap-2 p-2.5 rounded-lg border bg-muted/30",
                !readOnly && "cursor-pointer hover:bg-muted/50 transition-colors"
              )}
              onClick={() => !readOnly && setModelModalOpen(true)}
            >
              <div className="flex-1 min-w-0">
                {selectedModel ? (
                  <div className="space-y-1.5">
                    <div className="font-medium text-sm">{selectedModel.name}</div>
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-xs", providerColors[selectedModel.provider] || "")}>
                        {selectedModel.provider}
                      </Badge>
                      {SpeedIcon && speedInfo && (
                        <div className={cn("p-0.5 rounded", speedInfo.bgColor)}>
                          <SpeedIcon className={cn("h-3 w-3", speedInfo.color)} />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {config.modelId ? `Model: ${config.modelId}` : "No model selected"}
                  </span>
                )}
              </div>
              {!readOnly && (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          </div>

          {/* Prompt Editor Button */}
          <div className="space-y-1.5">
            <Label className="text-xs">System Prompt</Label>
            <div
              className={cn(
                "flex items-center gap-2 p-2.5 rounded-lg border bg-muted/30",
                !readOnly && "cursor-pointer hover:bg-muted/50 transition-colors"
              )}
              onClick={() => !readOnly && setPromptModalOpen(true)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm">
                    {isUsingCustomPrompt ? "Custom prompt" : "Default prompt"}
                  </span>
                  {isUsingCustomPrompt && (
                    <Badge className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                      Modified
                    </Badge>
                  )}
                </div>
                {isUsingCustomPrompt && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {currentPrompt.substring(0, 60)}...
                  </p>
                )}
              </div>
              {!readOnly && (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          </div>

          {/* Temperature and Max Tokens */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Temperature ({config.temperature})</Label>
              <Input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={config.temperature}
                onChange={(e) =>
                  onChange({ ...config, temperature: parseFloat(e.target.value) })
                }
                disabled={readOnly}
                className={cn("h-2", readOnly && "pointer-events-none")}
              />
              <p className="text-xs text-muted-foreground">
                Lower = more focused, Higher = more creative
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Max Tokens</Label>
              <Input
                type="number"
                min="1"
                max="16000"
                value={config.maxTokens}
                onChange={(e) =>
                  onChange({ ...config, maxTokens: parseInt(e.target.value) || 100 })
                }
                disabled={readOnly}
                className="h-8 text-sm"
              />
              <p className="text-xs text-muted-foreground">Maximum output length</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {!readOnly && (
        <>
          <ModelSelectorModal
            open={modelModalOpen}
            onOpenChange={setModelModalOpen}
            models={models}
            selectedModelId={config.modelId}
            onSelect={(modelId) => onChange({ ...config, modelId })}
            stageName={info.name}
          />
          <PromptEditorModal
            open={promptModalOpen}
            onOpenChange={setPromptModalOpen}
            stage={stage}
            prompt={currentPrompt}
            onSave={(prompt) => onChange({ ...config, prompt: prompt || undefined })}
          />
        </>
      )}
    </>
  );
}

// ============================================================================
// STAGE CONFIG SKELETON
// ============================================================================

function StageConfigSkeleton({ stage }: { stage: keyof typeof stageInfo }) {
  const info = stageInfo[stage];
  const Icon = info.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5" />
          {info.name}
        </CardTitle>
        <CardDescription>{info.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-3 w-48" />
            </div>
            <Skeleton className="h-8 w-16" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-3 w-40" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// SOURCE WEIGHT SKELETON
// ============================================================================

function SourceWeightSkeleton({ source }: { source: keyof typeof sourceInfo }) {
  const info = sourceInfo[source];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className={cn("text-base", info.color)}>{info.name}</CardTitle>
        <CardDescription className="text-xs">{info.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-12" />
          <Skeleton className="h-5 w-14 rounded-full" />
        </div>
        <Skeleton className="h-5 w-full" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>0.0</span>
          <span>1.0</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MODEL CARD SKELETON
// ============================================================================

function ModelCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-7 w-7 rounded" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />

        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Skeleton className="h-3 w-3" />
            <Skeleton className="h-5 w-20 rounded" />
            <Skeleton className="h-5 w-20 rounded" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// RAG PIPELINE TAB
// ============================================================================

function RAGPipelineTab() {
  const queryClient = useQueryClient();

  const { data: workflowConfigs, isLoading: configsLoading } = useQuery({
    queryKey: ["workflow-configs"],
    queryFn: getWorkflowConfigs,
  });

  const { data: availableModels, isLoading: modelsLoading } = useQuery({
    queryKey: ["available-models"],
    queryFn: getAvailableModels,
  });

  const { data: historyData } = useQuery({
    queryKey: ["workflow-history", "rag_workflow"],
    queryFn: () => getWorkflowConfigHistory("rag_workflow"),
  });

  // Dummy history data for preview (remove when API is deployed)
  const dummyHistoryData: WorkflowConfigHistoryItem[] = [
    {
      id: "wc_001",
      type: "rag_workflow",
      version: 5,
      data: {
        queryExpander: { modelId: "openrouter/amazon/nova-2-lite-v1:free", temperature: 0.1, maxTokens: 200 },
        assessor: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.2, maxTokens: 50 },
        reasoner: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.3, maxTokens: 2000 },
        formatter: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.1, maxTokens: 1500 },
      },
      notes: "Use Gemini Flash Lite for all phases - fast and reliable",
      isActive: true,
      createdAt: "2024-12-04T10:30:00.000Z",
      createdBy: { id: "user_1", username: "system", displayName: "system" },
    },
    {
      id: "wc_002",
      type: "rag_workflow",
      version: 4,
      data: {
        queryExpander: { modelId: "openrouter/amazon/nova-2-lite-v1:free", temperature: 0.1, maxTokens: 200 },
        assessor: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.2, maxTokens: 50 },
        reasoner: { modelId: "openrouter/deepseek/deepseek-r1", temperature: 0.3, maxTokens: 1500, prompt: "You are a helpful assistant analyzing Atlas restaurant system queries. Be thorough and cite sources." },
        formatter: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.1, maxTokens: 1500 },
      },
      notes: "Changed models for speed comparison",
      isActive: false,
      createdAt: "2024-12-04T08:00:00.000Z",
      createdBy: { id: "user_1", username: "system", displayName: "system" },
    },
    {
      id: "wc_003",
      type: "rag_workflow",
      version: 3,
      data: {
        queryExpander: { modelId: "openrouter/amazon/nova-2-lite-v1:free", temperature: 0.1, maxTokens: 200 },
        assessor: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.2, maxTokens: 50 },
        reasoner: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.3, maxTokens: 2000 },
        formatter: { modelId: "openrouter/google/gemini-2.5-flash-lite", temperature: 0.1, maxTokens: 1500 },
      },
      notes: "Bump up max tokens",
      isActive: false,
      createdAt: "2024-12-04T06:00:00.000Z",
      createdBy: { id: "user_1", username: "system", displayName: "system" },
    },
  ];

  // Toggle for showing dummy data when no real history exists
  const [showDummyData, setShowDummyData] = useState(false);
  const hasRealHistory = (historyData?.history?.length ?? 0) > 0;

  // Use real history data if available, otherwise show dummy data only if toggle is on
  const displayHistory = hasRealHistory
    ? historyData!.history
    : (showDummyData ? dummyHistoryData : []);

  const [localConfig, setLocalConfig] = useState<RAGWorkflowConfig | null>(null);
  const [notes, setNotes] = useState("");
  const [previewingVersion, setPreviewingVersion] = useState<WorkflowConfigHistoryItem | null>(null);

  // Default assessor config (used when config doesn't have assessor field)
  const defaultAssessorConfig: WorkflowStageConfig = {
    modelId: "amazon/nova-2-lite-v1:free",
    temperature: 0.2,
    maxTokens: 50,
  };

  // Default config for when no config exists (all default to Amazon Nova 2 Lite free tier)
  const defaultConfig: RAGWorkflowConfig = {
    queryExpander: {
      modelId: "amazon/nova-2-lite-v1:free",
      temperature: 0.3,
      maxTokens: 200,
    },
    assessor: defaultAssessorConfig,
    reasoner: {
      modelId: "amazon/nova-2-lite-v1:free",
      temperature: 0.3,
      maxTokens: 1500,
    },
    formatter: {
      modelId: "amazon/nova-2-lite-v1:free",
      temperature: 0.2,
      maxTokens: 800,
    },
  };

  // Ensure config has all required fields (handles legacy configs without assessor)
  const ensureCompleteConfig = (config: Partial<RAGWorkflowConfig> | null | undefined): RAGWorkflowConfig => {
    if (!config) return defaultConfig;
    return {
      queryExpander: config.queryExpander || defaultConfig.queryExpander,
      assessor: config.assessor || defaultAssessorConfig,
      reasoner: config.reasoner || defaultConfig.reasoner,
      formatter: config.formatter || defaultConfig.formatter,
    };
  };

  const currentConfig = ensureCompleteConfig(workflowConfigs?.configs?.ragWorkflow);
  // When previewing a version, show that version's data; otherwise show local changes or current config
  const displayConfig = previewingVersion
    ? ensureCompleteConfig(previewingVersion.data as Partial<RAGWorkflowConfig>)
    : (localConfig || currentConfig);
  const models = availableModels?.models || [];
  const isPreviewMode = previewingVersion !== null;

  const saveMutation = useMutation({
    mutationFn: (data: RAGWorkflowConfig) =>
      saveWorkflowConfig({
        type: "rag_workflow",
        data,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-configs"] });
      queryClient.invalidateQueries({ queryKey: ["workflow-history", "rag_workflow"] });
      setLocalConfig(null);
      setNotes("");
    },
  });

  const activateMutation = useMutation({
    mutationFn: activateWorkflowConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-configs"] });
      queryClient.invalidateQueries({ queryKey: ["workflow-history", "rag_workflow"] });
      setPreviewingVersion(null);
    },
  });

  const handleStageChange = (stage: keyof RAGWorkflowConfig, config: WorkflowStageConfig) => {
    const newConfig = { ...(localConfig || currentConfig), [stage]: config };
    setLocalConfig(newConfig);
  };

  const handleSelectVersion = (item: WorkflowConfigHistoryItem) => {
    if (item.isActive) {
      // Clicking active version exits preview mode
      setPreviewingVersion(null);
    } else {
      // Toggle preview for non-active versions
      setPreviewingVersion(previewingVersion?.id === item.id ? null : item);
    }
    // Clear any local edits when entering preview mode
    setLocalConfig(null);
  };

  const hasChanges = localConfig !== null;
  const isLoading = configsLoading || modelsLoading;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Configuration (2/3 width) */}
      <div className="lg:col-span-2 space-y-6">
        <Card className={cn(
          "border",
          isPreviewMode ? "ring-2 ring-blue-500 ring-inset" : "ring-0"
        )}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Multi-Model RAG
            </CardTitle>
            <CardDescription>
              Configure the models used in each stage of the RAG pipeline
            </CardDescription>
          </CardHeader>
          {!isPreviewMode && hasChanges && (
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Change Notes (optional)</Label>
                <Input
                  placeholder="Describe your changes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </CardContent>
          )}
          <div className={cn(
            "flex items-center justify-between gap-4 p-6 rounded-b-lg",
            isPreviewMode ? "bg-blue-500/10" : "bg-transparent"
          )}>
            {isPreviewMode ? (
              <>
                <Button
                  onClick={() => activateMutation.mutate(previewingVersion.id)}
                  disabled={activateMutation.isPending}
                >
                  {activateMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Rollback to v{previewingVersion.version}
                    </>
                  )}
                </Button>
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-sm">
                  <History className="h-4 w-4" />
                  <span className="font-medium">
                    Previewing v{previewingVersion.version}
                  </span>
                  {previewingVersion.notes && (
                    <span className="text-blue-600 dark:text-blue-300 hidden sm:inline">
                      — {previewingVersion.notes}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewingVersion(null)}
                    className="text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200 ml-2"
                  >
                    Exit
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <Button
                    onClick={() => saveMutation.mutate(displayConfig)}
                    disabled={!hasChanges || saveMutation.isPending}
                  >
                    {saveMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Configuration
                      </>
                    )}
                  </Button>
                  {hasChanges && (
                    <Button variant="outline" onClick={() => setLocalConfig(null)}>
                      Cancel
                    </Button>
                  )}
                </div>
                <div className="invisible flex items-center gap-2 text-sm">
                  <History className="h-4 w-4" />
                  <span className="font-medium">Previewing</span>
                  <Button variant="ghost" size="sm" className="ml-2">Exit</Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {saveMutation.isSuccess && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-700 dark:text-green-400 text-sm">
            Configuration saved successfully.
          </div>
        )}

        {saveMutation.isError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            Failed to save configuration: {(saveMutation.error as Error).message}
          </div>
        )}

        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {isLoading ? (
            <>
              {(["queryExpander", "assessor", "reasoner", "formatter"] as const).map((stage) => (
                <StageConfigSkeleton key={stage} stage={stage} />
              ))}
            </>
          ) : (
            <>
              {(["queryExpander", "assessor", "reasoner", "formatter"] as const).map((stage) => (
                <StageConfigEditor
                  key={stage}
                  stage={stage}
                  config={displayConfig[stage]}
                  models={models}
                  onChange={(config) => handleStageChange(stage, config)}
                  readOnly={isPreviewMode}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {/* Right Column - Version History (1/3 width) */}
      <div className="lg:col-span-1">
        <Card className="sticky top-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-4 w-4" />
              Version History
            </CardTitle>
            <CardDescription className="text-xs">
              {hasRealHistory
                ? "Click a version to preview its configuration"
                : "No versions saved yet"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasRealHistory && (
              <div className="flex items-center justify-between mb-4 p-2 rounded bg-muted/50">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {showDummyData ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  <span>Preview data</span>
                </div>
                <Switch
                  checked={showDummyData}
                  onCheckedChange={setShowDummyData}
                />
              </div>
            )}
            <div className="space-y-2">
              {displayHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {hasRealHistory ? "No versions found" : "Toggle preview data above to see example versions"}
                </p>
              ) : (
                displayHistory.map((item: WorkflowConfigHistoryItem) => {
                  const isSelected = previewingVersion?.id === item.id;
                  const isActive = item.isActive;
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelectVersion(item)}
                      className={cn(
                        "p-3 rounded border cursor-pointer transition-all",
                        isSelected && "ring-2 ring-primary border-primary bg-primary/5",
                        isActive && !isSelected && "bg-muted/30",
                        !isSelected && !isActive && "hover:bg-muted/50"
                      )}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">v{item.version}</span>
                          {isActive && <Badge className="text-xs">Active</Badge>}
                          {isSelected && <Badge variant="outline" className="text-xs">Previewing</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item.createdAt).toLocaleDateString()}{" "}
                          <span className="text-muted-foreground/70">
                            by {item.createdBy.displayName || item.createdBy.username || "system"}
                          </span>
                        </p>
                        {item.notes && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{item.notes}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// SOURCE WEIGHTS TAB
// ============================================================================

function SourceWeightsTab() {
  const queryClient = useQueryClient();

  const { data: workflowConfigs, isLoading: configsLoading } = useQuery({
    queryKey: ["workflow-configs"],
    queryFn: getWorkflowConfigs,
  });

  const { data: historyData } = useQuery({
    queryKey: ["workflow-history", "source_weights"],
    queryFn: () => getWorkflowConfigHistory("source_weights"),
  });

  // Dummy history data for preview (remove when API is deployed)
  const dummyHistoryData: WorkflowConfigHistoryItem[] = [
    {
      id: "sw_001",
      type: "source_weights",
      version: 2,
      data: { golden: 1.0, help: 0.9, linear: 0.5, notion: 0.3, slack: 0.2 },
      notes: "Increased Golden Answers weight after quality review",
      isActive: true,
      createdAt: "2024-12-04T06:00:00.000Z",
      createdBy: { id: "user_1", username: "ernest", displayName: "Ernest" },
    },
    {
      id: "sw_002",
      type: "source_weights",
      version: 1,
      data: { golden: 0.8, help: 0.7, linear: 0.6, notion: 0.5, slack: 0.4 },
      notes: "Default weights configuration",
      isActive: false,
      createdAt: "2024-12-02T10:00:00.000Z",
      createdBy: { id: "system", username: "system", displayName: null },
    },
  ];

  // Toggle for showing dummy data when no real history exists
  const [showDummyData, setShowDummyData] = useState(false);
  const hasRealHistory = (historyData?.history?.length ?? 0) > 0;

  // Use real history data if available, otherwise show dummy data only if toggle is on
  const displayHistory = hasRealHistory
    ? historyData!.history
    : (showDummyData ? dummyHistoryData : []);

  const [localWeights, setLocalWeights] = useState<SourceWeightsConfig | null>(null);
  const [notes, setNotes] = useState("");
  const [previewingVersion, setPreviewingVersion] = useState<WorkflowConfigHistoryItem | null>(null);

  const defaultWeights: SourceWeightsConfig = {
    golden: 1.0,
    help: 0.9,
    linear: 0.5,
    notion: 0.3,
    slack: 0.2,
  };

  const currentWeights = workflowConfigs?.configs?.sourceWeights || defaultWeights;
  // When previewing a version, show that version's data; otherwise show local changes or current config
  // Merge with defaults to handle incomplete/undefined data from history
  const displayWeights = previewingVersion
    ? { ...defaultWeights, ...(previewingVersion.data as Partial<SourceWeightsConfig>) }
    : (localWeights || currentWeights);
  const isPreviewMode = previewingVersion !== null;

  const saveMutation = useMutation({
    mutationFn: (data: SourceWeightsConfig) =>
      saveWorkflowConfig({
        type: "source_weights",
        data,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-configs"] });
      queryClient.invalidateQueries({ queryKey: ["workflow-history", "source_weights"] });
      setLocalWeights(null);
      setNotes("");
    },
  });

  const activateMutation = useMutation({
    mutationFn: activateWorkflowConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workflow-configs"] });
      queryClient.invalidateQueries({ queryKey: ["workflow-history", "source_weights"] });
      setPreviewingVersion(null);
    },
  });

  const handleWeightChange = (source: keyof SourceWeightsConfig, value: number) => {
    const newWeights = { ...(localWeights || currentWeights), [source]: value };
    setLocalWeights(newWeights);
  };

  const handleSelectVersion = (item: WorkflowConfigHistoryItem) => {
    if (item.isActive) {
      // Clicking active version exits preview mode
      setPreviewingVersion(null);
    } else {
      // Toggle preview for non-active versions
      setPreviewingVersion(previewingVersion?.id === item.id ? null : item);
    }
    // Clear any local edits when entering preview mode
    setLocalWeights(null);
  };

  const hasChanges = localWeights !== null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Configuration (2/3 width) */}
      <div className="lg:col-span-2 space-y-6">
        <Card className={cn(
          "border",
          isPreviewMode ? "ring-2 ring-blue-500 ring-inset" : "ring-0"
        )}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5" />
              Source Weights Configuration
            </CardTitle>
            <CardDescription>
              Adjust the ranking weights for different knowledge sources. Higher weights
              prioritize that source in search results.
            </CardDescription>
          </CardHeader>
          {!isPreviewMode && hasChanges && (
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Change Notes (optional)</Label>
                <Input
                  placeholder="Describe your changes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </CardContent>
          )}
          <div className={cn(
            "flex items-center justify-between gap-4 p-6 rounded-b-lg",
            isPreviewMode ? "bg-blue-500/10" : "bg-transparent"
          )}>
            {isPreviewMode ? (
              <>
                <Button
                  onClick={() => activateMutation.mutate(previewingVersion.id)}
                  disabled={activateMutation.isPending}
                >
                  {activateMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Rollback to v{previewingVersion.version}
                    </>
                  )}
                </Button>
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-sm">
                  <History className="h-4 w-4" />
                  <span className="font-medium">
                    Previewing v{previewingVersion.version}
                  </span>
                  {previewingVersion.notes && (
                    <span className="text-blue-600 dark:text-blue-300 hidden sm:inline">
                      — {previewingVersion.notes}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewingVersion(null)}
                    className="text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200 ml-2"
                  >
                    Exit
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <Button
                    onClick={() => saveMutation.mutate(displayWeights)}
                    disabled={!hasChanges || saveMutation.isPending}
                  >
                    {saveMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Weights
                      </>
                    )}
                  </Button>
                  {hasChanges && (
                    <Button variant="outline" onClick={() => setLocalWeights(null)}>
                      Cancel
                    </Button>
                  )}
                </div>
                <div className="invisible flex items-center gap-2 text-sm">
                  <History className="h-4 w-4" />
                  <span className="font-medium">Previewing</span>
                  <Button variant="ghost" size="sm" className="ml-2">Exit</Button>
                </div>
              </>
            )}
          </div>
        </Card>

        {saveMutation.isSuccess && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-700 dark:text-green-400 text-sm">
            Source weights saved successfully.
          </div>
        )}

        {saveMutation.isError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            Failed to save weights: {(saveMutation.error as Error).message}
          </div>
        )}

        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          {configsLoading ? (
            <>
              {(["golden", "help", "linear", "notion", "slack"] as const).map((source) => (
                <SourceWeightSkeleton key={source} source={source} />
              ))}
            </>
          ) : (
            <>
              {(["golden", "help", "linear", "notion", "slack"] as const).map((source) => {
                const info = sourceInfo[source];
                const weight = displayWeights[source];

                return (
                  <Card key={source} className={cn(isPreviewMode && "opacity-80")}>
                    <CardHeader className="pb-2">
                      <CardTitle className={cn("text-base", info.color)}>{info.name}</CardTitle>
                      <CardDescription className="text-xs">{info.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold">{weight.toFixed(1)}</span>
                        <Badge
                          variant={weight >= 0.8 ? "default" : weight >= 0.5 ? "secondary" : "outline"}
                        >
                          {weight >= 0.8 ? "High" : weight >= 0.5 ? "Medium" : "Low"}
                        </Badge>
                      </div>
                      <Input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={weight}
                        onChange={(e) =>
                          handleWeightChange(source, parseFloat(e.target.value))
                        }
                        className={cn("w-full", isPreviewMode && "pointer-events-none")}
                        disabled={isPreviewMode}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0.0</span>
                        <span>1.0</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Right Column - Version History (1/3 width) */}
      <div className="lg:col-span-1">
        <Card className="sticky top-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-4 w-4" />
              Version History
            </CardTitle>
            <CardDescription className="text-xs">
              {hasRealHistory
                ? "Click a version to preview its configuration"
                : "No versions saved yet"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasRealHistory && (
              <div className="flex items-center justify-between mb-4 p-2 rounded bg-muted/50">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {showDummyData ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  <span>Preview data</span>
                </div>
                <Switch
                  checked={showDummyData}
                  onCheckedChange={setShowDummyData}
                />
              </div>
            )}
            <div className="space-y-2">
              {displayHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {hasRealHistory ? "No versions found" : "Toggle preview data above to see example versions"}
                </p>
              ) : (
                displayHistory.map((item: WorkflowConfigHistoryItem) => {
                  const isSelected = previewingVersion?.id === item.id;
                  const isActive = item.isActive;
                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelectVersion(item)}
                      className={cn(
                        "p-3 rounded border cursor-pointer transition-all",
                        isSelected && "ring-2 ring-primary border-primary bg-primary/5",
                        isActive && !isSelected && "bg-muted/30",
                        !isSelected && !isActive && "hover:bg-muted/50"
                      )}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">v{item.version}</span>
                          {isActive && <Badge className="text-xs">Active</Badge>}
                          {isSelected && <Badge variant="outline" className="text-xs">Previewing</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item.createdAt).toLocaleDateString()}{" "}
                          <span className="text-muted-foreground/70">
                            by {item.createdBy.displayName || item.createdBy.username || "system"}
                          </span>
                        </p>
                        {item.notes && (
                          <p className="text-xs text-muted-foreground line-clamp-2">{item.notes}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN SETTINGS PAGE
// ============================================================================

export function SettingsPage() {
  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure AI models, RAG pipeline, and source weights
          </p>
        </div>

        <Tabs defaultValue="rag-pipeline" className="space-y-6">
          <TabsList>
            <TabsTrigger value="rag-pipeline" className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              Multi-Model RAG
            </TabsTrigger>
            <TabsTrigger value="source-weights" className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Source Weights
            </TabsTrigger>
            <TabsTrigger value="models" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Single Model RAG (Legacy)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rag-pipeline">
            <RAGPipelineTab />
          </TabsContent>

          <TabsContent value="source-weights">
            <SourceWeightsTab />
          </TabsContent>

          <TabsContent value="models">
            <ModelsTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
